/**
 * product-media-video.js — play/pause for product gallery videos.
 *
 * <product-media-video> wraps one <video> + one transparent toggle button (see
 * snippets/product-media-item.liquid). Because the behavior lives on the shared media snippet,
 * both the zoom overlay and the (later) inline gallery get it for free.
 *
 * Deliberately NOT a reuse of editorial-video.js: that one is scroll-driven autoplay grouped by
 * ROW (so a side-by-side pair plays together) and it binds to .editorial-media--video. Reusing it
 * would drag editorial's class names into the product gallery and break Shopify's per-page CSS
 * subsetting. This is the same interaction pattern, minus the row grouping we don't need.
 *
 * Behavior: autoplay when scrolled into view, pause when out, click anywhere to toggle. A manual
 * pause sticks until the video leaves view and comes back. Videos are muted (autoplay policy).
 * prefers-reduced-motion: never autoplay; the user can still press play.
 */
(function () {
  if (customElements.get('product-media-video')) return;

  var CONTROLS_MS = 1500;

  class ProductMediaVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      this.userPaused = false;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);

      this.video.addEventListener('play', this.syncState.bind(this));
      this.video.addEventListener('pause', this.syncState.bind(this));
      this.syncState();

      this.setupInView();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.observer) this.observer.disconnect();
      clearTimeout(this.controlsTimer);
    }

    onClick(event) {
      event.preventDefault();

      // Flash the control on tap. This replaces the :hover reveal on touch, where :hover
      // would stick after the tap — the CSS gates hover behind @media (hover: hover).
      this.classList.add('show-controls');
      clearTimeout(this.controlsTimer);
      var self = this;
      this.controlsTimer = setTimeout(function () {
        self.classList.remove('show-controls');
      }, CONTROLS_MS);

      if (this.video.paused) {
        this.userPaused = false;
        var p = this.video.play();
        if (p && p.catch) p.catch(function () {});
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    }

    syncState() {
      var playing = !this.video.paused && !this.video.ended;
      this.classList.toggle('is-playing', playing);
      this.classList.toggle('is-paused', !playing);
    }

    /** Autoplay while in view; pause on the way out (and clear a sticky manual pause). */
    setupInView() {
      if (typeof IntersectionObserver === 'undefined') return;
      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              if (self.reduceMotion || self.userPaused) return;
              var p = self.video.play();
              if (p && p.catch) p.catch(function () {});
            } else {
              if (!self.video.paused) self.video.pause();
              // Leaving view resets the manual pause, so scrolling back re-autoplays.
              self.userPaused = false;
            }
          });
        },
        { threshold: [0, 0.6] }
      );
      this.observer.observe(this);
    }
  }

  customElements.define('product-media-video', ProductMediaVideo);
})();
